import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import { useSettingValue } from 'app/profile/settings/utils/useSettingValue';
import CodeMirror from 'codemirror';
import ActionButton from 'common/components/ActionButton';
import CustomSelectValidator from 'common/components/CustomSelectValidators';
import CustomSunEditor from 'common/components/CustomSunEditor';
import CustomTextValidator from 'common/components/CustomTextValidators';
import { transformKey } from 'common/features/extraFields/ExtraFieldsRender';
import DateInput from 'common/features/filters/components/DateInput';
import useFlexBoxStyles from 'common/flexBox';
import useOnliveStyles from 'common/onliveStyles';
import { cleanContent } from 'common/utils/cleanContent';
import { isBefore } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCalendarsAction, fetchServicesAction, getAppointmentsSavePending } from 'redux/slices/iCalendarV4';
import { makeStyles } from 'tss-react/mui';

import CustomIconButton from 'common/components/CustomIconButton';
import CustomTooltip from 'common/components/CustomTooltip';
import CalendarType from 'common/enums/calendarTypes';
import { openNestedDialog } from 'redux/slices/nestedDialogSlice';
import CustomPaginatedSelect from '../../../../common/components/CustomPaginatedSelect';
import AppointmentFormAvailability from './AppointmentFormAvailability';
import AppointmentFormSettingsStatus from './AppointmentFormSettingsStatus';

const useStyles = makeStyles()((theme) => ({
    minH9: {
        minHeight: theme.spacing(9),
    },
    border: {
        border: '1px solid #E2E2E2',
        padding: '10px',
        marginBottom: '10px',
        borderRadius: theme.spacing(2),
    },
    rescheduleContainer: {
        position: 'absolute',
        width: '100%',
        background: '#47474780',
        height: '100%',
        left: '0',
        top: '0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: '100',

        '& ol-calendar': {
            width: '85%',
            '& #calendar': {
                width: '100%',
            },
        },
    },
    timeSelector: {
        width: '80%',
    },

    selector: {
        maxWidth: '200px',
    },
    calendar: {
        backgroundColor: '#fff',
        padding: theme.spacing(4),
        borderRadius: theme.spacing(2),
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        textAlign: 'center',
        width: '55%',
        maxHeight: '80%',
        overflowY: 'auto',
    },
    ownerSelector: {
        width: '80%',
    },

    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        alignItems: 'center',
        width: '100%',
    },
    filter: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        width: '80%',
        justifyContent: 'space-between',
    },

    actions: {
        display: 'flex',
        gap: theme.spacing(1),
    },
}));

const editorConfig = {
    codeMirror: {
        src: CodeMirror,
        options: {
            mode: { name: 'handlebars', base: 'text/html' },
            theme: 'material',
            lineNumbers: true,
        },
    },
    buttonList: [
        [
            'undo',
            'redo',
            'font',
            'fontSize',
            'formatBlock',
            'paragraphStyle',
            'blockquote',
            'bold',
            'underline',
            'italic',
            'strike',
            'subscript',
            'superscript',
            'fontColor',
            'hiliteColor',
            'textStyle',
            'removeFormat',
            'outdent',
            'indent',
            'align',
            'horizontalRule',
            'list',
            'lineHeight',
            'link',
            'fullScreen',
            'showBlocks',
            //'codeView',
            'preview',
        ],
    ],
    resizingBar: false,
    height: 200,
};

const AppointmentFormSettings = (props) => {
    const {
        fields,
        handleFieldChange,
        setStep,
        step,
        cancel,
        appointment,
        isValidForm,
        service,
        availabilitySelectedCalendars,
        setAvailabilitySelectedCalendars,
    } = props;

    const { classes } = useStyles();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { classes: onlive } = useOnliveStyles();
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [showAgentEditor, setShowAgentEditor] = useState(false);
    const [showDescriptionEditor, setShowDescriptionEditor] = useState(false);
    const [sunError1, setSunError1] = useState(false);
    const [sunError2, setSunError2] = useState(false);
    const savePending = useSelector(getAppointmentsSavePending);
    const originSetting = useSettingValue(SettingsKey.APPOINTMENT_ORIGIN) || {};
    const [userGroupId, setUserGroupId] = useState(null);

    const ref = useRef();

    const { startAt, endAt } = fields;
    const dispatch = useDispatch();

    const handleDateChange = useCallback(
        (e, name) => {
            handleFieldChange({
                target: {
                    name: name,
                    value: e,
                },
            });
        },
        [handleFieldChange],
    );

    useEffect(() => {
        if (fields.serviceId === '' && fields.agent === '' && fields.asset === '') {
            setUserGroupId(null);
        }
    }, [fields]);

    const updateCalendar = useCallback(
        (calendarId, type, item) => {
            if (item) {
                setAvailabilitySelectedCalendars((prev) => {
                    const filtered = prev.filter((c) => c.type !== type);
                    return [...filtered, item];
                });
                setUserGroupId(item?.userGroupId || null);
            }
            handleFieldChange({
                target: {
                    name: type,
                    value: calendarId ?? '',
                },
            });
        },
        [handleFieldChange, setAvailabilitySelectedCalendars],
    );

    const handleSelectedAvailability = useCallback(
        (availability) => {
            const { startAt, endAt, calendars } = availability;
            handleDateChange(startAt, 'startAt');
            handleDateChange(endAt, 'endAt');
            setAvailabilitySelectedCalendars(calendars);
            updateCalendar(calendars.find((c) => c.type === CalendarType.AGENT)?.canonicalId, CalendarType.AGENT);
            updateCalendar(calendars.find((c) => c.type === CalendarType.ASSET)?.canonicalId, CalendarType.ASSET);
        },
        [handleDateChange, updateCalendar, setAvailabilitySelectedCalendars],
    );

    const openViewToFindAvailability = () => {
        dispatch(
            openNestedDialog(
                <AppointmentFormAvailability serviceId={fields.serviceId} onConfirm={handleSelectedAvailability} />,
            ),
        );
    };

    const updateField = useCallback(
        (name, value) => {
            handleFieldChange({
                target: {
                    name,
                    value,
                },
            });
        },
        [handleFieldChange],
    );

    // const [inputError, setInputError] = useState({ appointmentExtraFields: null });

    const handleNextStep = () => {
        if (isValidForm()) {
            setStep(step + 1);
        } else {
            enqueueSnackbar(t('app.common.formError'), { variant: 'error' });
        }
    };

    const renderServiceLabel = (service) => {
        return service?.name + (service?.userGroup?.name ? ` (${service?.userGroup?.name})` : '');
    };

    const renderAgentLabel = (agent) => {
        return `${agent?.extraFields?.firstName} (${agent.email})`;
    };

    const getAssetLabel = (asset) => {
        return asset?.name + (asset?.extraFields?.resourceId ? ` (${asset?.extraFields?.resourceId})` : '');
    };

    const getCalendarItems = (type) => {
        const items = [];
        if (availabilitySelectedCalendars.length > 0) {
            items.push(...availabilitySelectedCalendars.filter((item) => item.type === type));
        } else if (appointment.calendars) {
            items.push(...appointment.calendars.filter((item) => item.type === type));
        }
        return items;
    };
    return (
        <ValidatorForm
            ref={ref}
            onSubmit={handleNextStep}
            onError={(e) => {
                enqueueSnackbar(t('app.common.formError'), { variant: 'error' });
            }}
            className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
        >
            <Stack direction="column" className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone, flexBox.h100)}>
                <Stack direction="column" spacing={2}>
                    <FormGroup>
                        <FormLabel>{t('appointment.form.title')}</FormLabel>
                        <CustomTextValidator
                            name="title"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            autoComplete="new-name"
                            value={fields.title}
                            onChange={handleFieldChange}
                            validators={['required', 'maxStringLength: 100']}
                            errorMessages={[t('app.common.required'), t('appointment.form.titleMaxLength')]}
                            useRegExpText={false}
                        />
                    </FormGroup>

                    <FormGroup className={(flexBox.borderRadius50, classes.border)}>
                        <Grid container direction={'column'} marginBottom={2}>
                            <FormLabel>{t('iCalendar.appointmentForm.selectService')}</FormLabel>
                            <CustomPaginatedSelect
                                {...props}
                                id={fields.serviceId}
                                items={service ? [service] : appointment.service ? [appointment.service] : []}
                                type="service"
                                fetchAction={fetchServicesAction}
                                label={renderServiceLabel}
                                i18nKeys={{
                                    searchPlaceholder: t('contactRequest.searchServices'),
                                    none: t('contactRequest.noServices'),
                                }}
                                selectNone={true}
                                fullWidth={true}
                                size="big"
                                variant="outlined"
                                onChange={(e, _, item) => {
                                    handleFieldChange({
                                        target: {
                                            name: 'serviceId',
                                            value: e.target.value ?? '',
                                        },
                                    });
                                    setUserGroupId(item?.userGroupId || null);
                                }}
                                queryParams={{
                                    expand: ['userGroup'], // TODO: Only if user manage different groups
                                    //calendars: [fields.agent, fields.asset].filter(Boolean),
                                    ...(fields.asset || fields.agent ? { userGroupId } : {}),
                                }}
                            />
                        </Grid>

                        <FormLabel>{t('iCalendar.appointmentForm.time')}</FormLabel>

                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
                            <FormControl>
                                <DateInput
                                    id="basic-date-range-start-date"
                                    type={'date-time'}
                                    size="big"
                                    value={startAt}
                                    disabled={Boolean(appointment?.id)}
                                    onChange={(date) => handleDateChange(date, 'startAt')}
                                />

                                {!startAt && (
                                    <FormHelperText error>{t('license.licenseForm.emailRequired')}</FormHelperText>
                                )}
                            </FormControl>

                            <Typography variant="body1">{t('services.form.dateTo')}</Typography>

                            <FormControl>
                                <DateInput
                                    id="basic-date-range-end-date"
                                    type={'date-time'}
                                    value={endAt}
                                    disabled={Boolean(appointment?.id)}
                                    onChange={(date) => {
                                        handleDateChange(date, 'endAt');
                                    }}
                                    minDate={startAt ? new Date(startAt) : undefined}
                                />

                                {!endAt && (
                                    <FormHelperText error>{t('license.licenseForm.emailRequired')}</FormHelperText>
                                )}

                                {isBefore(new Date(startAt), new Date(endAt)) ? null : (
                                    <FormHelperText error>
                                        {t('iCalendar.appointmentForm.startBeforeEnd')}
                                    </FormHelperText>
                                )}
                            </FormControl>
                            {!fields.skipAvailabilityValidation && (
                                <CustomTooltip
                                    arrow
                                    title={t('iCalendar.appointmentForm.findAvailability')}
                                    aria-label="find availability"
                                    placement="bottom"
                                >
                                    <CustomIconButton
                                        id="onlive-button-find-availability"
                                        onClick={openViewToFindAvailability}
                                        disabled={Boolean(!fields.serviceId)}
                                        hideBorder={false}
                                        color="primary"
                                        sx={{
                                            bgcolor: 'action.hover',
                                            '&:hover': {
                                                bgcolor: 'action.selected',
                                            },
                                        }}
                                    >
                                        <EventAvailableIcon />
                                    </CustomIconButton>
                                </CustomTooltip>
                            )}
                        </Stack>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        className={cx(flexBox.padding0, flexBox.margin01)}
                                        checked={fields.skipAvailabilityValidation}
                                        onChange={(e) => handleFieldChange(e, 'skipAvailabilityValidation')}
                                        name="skipAvailabilityValidation"
                                        color="primary"
                                    />
                                }
                                label={
                                    <FormLabel>{t('iCalendar.appointmentForm.skipAvailabilityValidation')}</FormLabel>
                                }
                            />
                        </FormGroup>

                        <Grid container direction={'column'} marginTop={2}>
                            <FormLabel>{t('iCalendar.appointmentForm.selectOwner')}</FormLabel>
                            <CustomPaginatedSelect
                                {...props}
                                id={fields.agent ?? null}
                                type="agent"
                                fetchAction={fetchCalendarsAction}
                                label={renderAgentLabel}
                                i18nKeys={{
                                    searchPlaceholder: t('iCalendar.filter.card.searchAgents'),
                                    none: t('contactRequest.noAgent'),
                                }}
                                selectNone={true}
                                fullWidth={true}
                                size="big"
                                variant="outlined"
                                onChange={(e, type, item) => {
                                    updateCalendar(e.target.value, type, item);
                                }}
                                queryParams={{
                                    type: 'agent',
                                    serviceId: fields.serviceId || undefined,
                                    expand: ['userGroup'], // TODO: Only if user manage different groups
                                    userGroupId,
                                }}
                                primaryId="canonicalId"
                                items={getCalendarItems(CalendarType.AGENT)}
                            />
                        </Grid>
                        <Grid container direction={'column'} marginTop={2}>
                            <FormLabel>{t('iCalendar.appointmentForm.selectAsset')}</FormLabel>
                            <CustomPaginatedSelect
                                {...props}
                                id={fields.asset ?? null}
                                type="asset"
                                fetchAction={fetchCalendarsAction}
                                label={getAssetLabel}
                                i18nKeys={{
                                    searchPlaceholder: t('iCalendar.filter.searchAssets'),
                                    none: t('iCalendar.appointmentForm.noAssetSelected'),
                                }}
                                selectNone={true}
                                fullWidth={true}
                                size="big"
                                variant="outlined"
                                onChange={(e, type, item) => {
                                    updateCalendar(e.target.value, type, item);
                                }}
                                queryParams={{
                                    type: 'asset',
                                    serviceId: fields.serviceId || undefined,
                                    userGroupId,
                                }}
                                primaryId="canonicalId"
                                items={getCalendarItems(CalendarType.ASSET)}
                            />
                        </Grid>
                        {/* {appointment?.serviceId && (
                            <ActionButton
                                color="primary"
                                autoFocus
                                fitWidth
                                onClick={() => setRescheduleOpen(true)}
                                disabled={savePending}
                            >
                                {t('iCalendar.appointmentForm.reschedule')}
                            </ActionButton>
                        )} */}
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{t('appointment.form.description')}</FormLabel>
                        {showDescriptionEditor ? (
                            <CustomSunEditor
                                rows="4"
                                defaultValue={fields.description}
                                setOptions={editorConfig}
                                onChange={(value) => updateField('description', value)}
                                onBlur={() => setShowDescriptionEditor(false)}
                                sunError={setSunError1}
                            />
                        ) : (
                            <Stack
                                aria-hidden={true}
                                onClick={() => setShowDescriptionEditor(true)}
                                className={cx(
                                    flexBox.padding01,
                                    flexBox.borderRadius2,
                                    onlive.borderMediumGray,
                                    classes.minH9,
                                )}
                            >
                                <Typography
                                    dangerouslySetInnerHTML={{
                                        __html: cleanContent(fields.description),
                                    }}
                                />
                                {sunError1 ? (
                                    <FormHelperText error>{t('app.common.sunEditorError')}</FormHelperText>
                                ) : null}
                            </Stack>
                        )}
                    </FormGroup>

                    <AppointmentFormSettingsStatus fields={fields} handleFieldChange={handleFieldChange} />

                    <FormGroup>
                        <FormLabel>{t('appointments.table.source')}</FormLabel>

                        <CustomSelectValidator
                            name="origin"
                            select
                            size="small"
                            fullWidth={true}
                            value={fields.origin}
                            //placeHolder={t('appointments.form.selectOrigin')}
                            onChange={handleFieldChange}
                            validators={['required']}
                            errorMessages={[t('app.common.required')]}
                            variant="outlined"
                            disabled={
                                !originSetting?.[fields.origin]?.manual &&
                                fields.origin !== 'vms' &&
                                fields.origin !== ''
                            }
                        >
                            {fields.origin === '' ||
                            fields.origin === 'vms' ||
                            originSetting.hasOwnProperty(fields.origin) ? null : (
                                <MenuItem value={fields.origin} key={fields.origin}>
                                    {transformKey(fields.origin)}
                                </MenuItem>
                            )}
                            {Object.entries(originSetting)
                                .filter(([key, value]) => value?.manual || key === fields.origin)
                                .map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value.label[i18n.language]}
                                    </MenuItem>
                                ))}
                            <MenuItem value="vms" key="vms">
                                {t('appointments.form.vmsOrigin')}
                            </MenuItem>
                        </CustomSelectValidator>
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{t('iCalendar.appointmentForm.agentComent')}</FormLabel>

                        {showAgentEditor ? (
                            <CustomSunEditor
                                rows="4"
                                defaultValue={fields.comment}
                                setOptions={editorConfig}
                                onChange={(value) => updateField('comment', value)}
                                onBlur={() => setShowAgentEditor(false)}
                                sunError={setSunError2}
                            />
                        ) : (
                            <Stack
                                aria-hidden={true}
                                onClick={() => setShowAgentEditor(true)}
                                className={cx(
                                    flexBox.padding01,
                                    flexBox.borderRadius2,
                                    onlive.borderMediumGray,
                                    classes.minH9,
                                )}
                            >
                                <Typography
                                    dangerouslySetInnerHTML={{
                                        __html: cleanContent(fields.comment),
                                    }}
                                />
                                {sunError2 ? (
                                    <FormHelperText error>{t('app.common.sunEditorError')}</FormHelperText>
                                ) : null}
                            </Stack>
                        )}
                    </FormGroup>
                    {/* <FormGroup>
                        <FormLabel>{t('services.form.extraFields')}</FormLabel>
                        <JsonCodeInputMonacoEditor
                            name="appointmentExtraFields"
                            value={fields?.extraFields || ''}
                            onChange={(value, errors) => {
                                const codeHaveErrors = errors && Array.isArray(errors) && errors.length > 0;
                                const newErrors = codeHaveErrors ? errors : null;
                                setInputError({ appointmentExtraFields: newErrors });
                                updateField('extraFields', value);
                            }}
                        />
                        <SimpleCodeErrorView
                            id={'error-context'}
                            mainErrorMessage={t('JsonError')}
                            errorList={inputError['appointmentExtraFields']}
                        />
                    </FormGroup> */}
                </Stack>
            </Stack>

            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                className={flexBox.paddingTop2}
            >
                <ActionButton onClick={cancel} color="default" fitWidth disabled={savePending}>
                    {t('multimedia.multimediaForm.cancel')}
                </ActionButton>

                <ActionButton
                    color="primary"
                    autoFocus
                    fitWidth
                    type="submit"
                    disabled={savePending}
                    loading={String(savePending)}
                >
                    {t('event.eventFormSettings.save')}
                </ActionButton>
            </Stack>
        </ValidatorForm>
    );
};

export default AppointmentFormSettings;
